import type { Principal } from '@ampshift/shared';
import { createStore, readJson, safeStorage, writeJson } from '../store';
import { getSessionId, resetSessionId } from './session';

export type AuthState =
  | { status: 'signedOut'; reason?: 'expired' | 'user' }
  | { status: 'cognito'; idToken: string; expiresAt: number; username: string }
  | { status: 'dev'; principal: Principal };

const KEY = 'ampshift.auth';

function load(): AuthState {
  const saved = readJson<AuthState>(safeStorage('session'), KEY);
  if (!saved) return { status: 'signedOut' };
  if (saved.status === 'cognito' && saved.expiresAt < Date.now() - 60 * 60 * 1000) return { status: 'signedOut', reason: 'expired' };
  return saved;
}

export const authStore = createStore<AuthState>(load());

type SignOutListener = (reason: 'expired' | 'user') => void;
const signOutListeners = new Set<SignOutListener>();

/** Subscribers (voice capture, speaker, conversation) are notified before the state flips. */
export function onSignOut(listener: SignOutListener): () => void {
  signOutListeners.add(listener);
  return () => {
    signOutListeners.delete(listener);
  };
}

export function setAuthState(state: AuthState): void {
  if (state.status === 'signedOut') writeJson(safeStorage('session'), KEY, undefined);
  else writeJson(safeStorage('session'), KEY, state);
  authStore.set(state);
}

export function signOutLocally(reason: 'expired' | 'user'): void {
  for (const l of Array.from(signOutListeners)) {
    try {
      l(reason);
    } catch {
      /* listeners must not block sign-out */
    }
  }
  setAuthState({ status: 'signedOut', reason });
  resetSessionId();
}

export function isDevAuthEnabled(): boolean {
  return import.meta.env.VITE_DEV_AUTH === '1' || import.meta.env.VITE_DEV_AUTH === 'true';
}

/** Headers that identify the caller: Bearer ID token in production, X-Dev-Principal JSON locally. */
export function getAuthHeaders(state: AuthState = authStore.get()): Record<string, string> {
  const headers: Record<string, string> = { 'X-Session-Id': getSessionId() };
  if (state.status === 'cognito') headers.Authorization = `Bearer ${state.idToken}`;
  else if (state.status === 'dev') headers['X-Dev-Principal'] = JSON.stringify({ ...state.principal, sessionId: getSessionId() });
  return headers;
}
